import { useState } from "react";
import Button from "@components/ui/Button";
import Spinner from "@components/ui/Spinner";
import SelectField from "@components/form/SelectField";

function StatusPill({ connected }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        connected ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-700"
      }`}
    >
      {connected ? "Connected" : "Not connected"}
    </span>
  );
}

/**
 * Sikka PMS integration.
 *
 * Unlike NexHealth, the admin doesn't type credentials — they pick one of the
 * Sikka offices our account is authorized for, and "Connect & Sync" persists the
 * office's config and pulls patients/providers in one backend call.
 */
export default function SikkaEditor({
  provider,            // clinic.active_pms_provider
  config,              // clinic.pms_config
  practices,           // [{ office_id, practice_name, practice_management_system, domain, city, state }]
  practicesLoading,
  onLoadPractices,
  onConnect,           // (officeId) => Promise
  connecting,
}) {
  const [selectedOffice, setSelectedOffice] = useState("");

  const connected = provider === "sikka" && !!config?.office_id;
  const hasPractices = Array.isArray(practices) && practices.length > 0;

  const options = (practices || []).map((p) => ({
    value: p.office_id,
    label: `${p.office_id} — ${p.practice_name || "Unnamed"}${
      p.practice_management_system ? ` (${p.practice_management_system})` : ""
    }`,
  }));

  return (
    <div className="border rounded-lg p-4 bg-white space-y-3">
      <div>
        <div className="text-sm font-semibold flex items-center gap-2">
          Sikka PMS Integration
          <StatusPill connected={connected} />
        </div>
        <div className="text-xs text-gray-600 mt-1">
          Connect this clinic to a Sikka office. Connecting sets this clinic's
          active PMS to Sikka and syncs its patients and providers.
        </div>
      </div>

      {connected && (
        <div className="text-xs text-gray-700 bg-gray-50 rounded-md px-3 py-2">
          Connected to office <span className="font-medium">{config.office_id}</span>
          {config.practice_management_system
            ? ` · ${config.practice_management_system}`
            : ""}
        </div>
      )}

      {!hasPractices ? (
        <Button
          size="sm"
          color="light"
          disabled={practicesLoading}
          onClick={onLoadPractices}
        >
          {practicesLoading ? (
            <span className="flex items-center gap-2">
              <Spinner size="sm" /> Loading offices…
            </span>
          ) : (
            "Load Sikka offices"
          )}
        </Button>
      ) : (
        <>
          <SelectField
            label="Sikka office"
            options={options}
            value={selectedOffice}
            onChange={(e) => setSelectedOffice(e.target.value)}
            placeholder="-- Select a Sikka office --"
          />
          <Button
            size="sm"
            color="green"
            disabled={connecting || !selectedOffice || selectedOffice === " "}
            onClick={() => onConnect(selectedOffice)}
          >
            {connecting ? "Connecting & syncing…" : "Connect & Sync"}
          </Button>
        </>
      )}

    </div>
  );
}
